import { DetailKpiCard } from '@bestseller/retail-planning.ui.atoms.detail-kpi-card';
import { Grid, styled } from '@mui/material';
import React from 'react';
import { ScopeEnum } from 'src/domain';
import { StoreDetails } from 'src/hooks/turnover/useTurnoverDetails';
import { numberWithSpaces } from 'src/utils/numberWithSpaces';

export type ComparableValue<T> = T | readonly [T, T] | readonly [T, undefined];

export const handleKpiNodeValue = (value: ComparableValue<number>) => {
    if (Array.isArray(value)) {
        if (value[1] && value[1] !== value[0]) {
            const firstValue = `(${numberWithSpaces(Math.round(value[0]))})`;
            const secondValue = numberWithSpaces(Math.round(value[1]));
            return `${firstValue} ${secondValue}`;
        } else {
            return numberWithSpaces(Math.round(value[0]));
        }
    } else {
        return numberWithSpaces(Math.round(Number(value)));
    }
};

export const formatWithSquareMeters = (value: number | string): string => {
    return `${value}m²`;
};

export interface KpiItem {
    title: string;
    value: ComparableValue<number>;
}

export interface TurnoverKpisProps {
    storeDetails: StoreDetails | undefined;
    scope: ScopeEnum | undefined;
    expectedSales: KpiItem | undefined;
    expectedSalesPerSquareMeter: KpiItem | undefined;
    averageIndex: ComparableValue<number>;
    salesFloorSquareMeters: KpiItem | undefined;
    numberOfStores: ComparableValue<number>;
}

export const TurnoverKpis: React.FC<TurnoverKpisProps> = ({
    storeDetails,
    scope: scope,
    expectedSales,
    expectedSalesPerSquareMeter,
    averageIndex,
    salesFloorSquareMeters,
    numberOfStores,
}) => {
    return (
        <Grid
            container
            spacing={2}
            sx={{
                alignItems: 'stretch',
            }}
        >
            {expectedSales && (
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4,
                        xl: 12 / 5,
                    }}
                >
                    <StyledDetailedKPICard
                        title={expectedSales.title}
                        value={handleKpiNodeValue(expectedSales.value)}
                        sx={{ flexGrow: 1, flexBasis: 0, overflow: 'hidden' }}
                    />
                </Grid>
            )}
            {expectedSalesPerSquareMeter && (
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4,
                        xl: 12 / 5,
                    }}
                >
                    <StyledDetailedKPICard
                        title={expectedSalesPerSquareMeter.title}
                        value={handleKpiNodeValue(expectedSalesPerSquareMeter.value)}
                        sx={{ flexGrow: 1, flexBasis: 0, overflow: 'hidden' }}
                    />
                </Grid>
            )}
            <Grid
                size={{
                    xs: 12,
                    sm: 6,
                    md: 6,
                    lg: 4,
                    xl: 12 / 5,
                }}
            >
                <StyledDetailedKPICard
                    title={'AVG INDEX NEXT 12 MONTHS'}
                    value={handleKpiNodeValue(averageIndex)}
                    sx={{ flexGrow: 1, flexBasis: 0, overflow: 'hidden' }}
                />
            </Grid>
            {salesFloorSquareMeters && (
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4,
                        xl: 12 / 5,
                    }}
                >
                    <StyledDetailedKPICard
                        title={salesFloorSquareMeters.title}
                        value={salesFloorSquareMeters.value}
                        valueFormatter={formatWithSquareMeters}
                        sx={{ flexGrow: 1, flexBasis: 0, overflow: 'hidden' }}
                    />
                </Grid>
            )}
            {storeDetails?.clusterName && (
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4,
                        xl: 12 / 5,
                    }}
                >
                    <StyledDetailedKPICard
                        title={'CLUSTER'}
                        value={storeDetails.clusterName}
                        sx={{ flexGrow: 1, flexBasis: 0, overflow: 'hidden' }}
                    />
                </Grid>
            )}
            {scope !== ScopeEnum.STORE && (
                <Grid
                    size={{
                        xs: 12,
                        sm: 6,
                        md: 6,
                        lg: 4,
                        xl: 12 / 5,
                    }}
                >
                    <StyledDetailedKPICard
                        title={'NUMBER OF STORES'}
                        value={numberOfStores}
                        sx={{ flexGrow: 1, flexBasis: 0, overflow: 'hidden' }}
                    />
                </Grid>
            )}
        </Grid>
    );
};

export const StyledDetailedKPICard = styled(DetailKpiCard)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
    boxSizing: 'border-box',
    '& .MuiTypography-h2': {
        fontFamily: 'Inter',
        textTransform: 'none',
    },
    '& .MuiTypography-h4': {
        ...theme.typography.subtitle1,
        fontWeight: 'bold',
        wordWrap: 'break-word',
        whiteSpace: 'normal',
    },
}));
